/* eslint-disable max-len */
// src/components/teams/AcceptTeamInvite.js
// Handles team invite acceptance flow:
// - Authenticated + email matches: auto-accept
// - Authenticated + email mismatch: error
// - Not authenticated: prompt to sign up/login

import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { httpsCallable } from "firebase/functions";
import { functions } from '../../firebaseConfig';
import { useAuthUser } from '../../hooks/useAuthUser';
import { FiUsers, FiLoader, FiAlertCircle, FiCheckCircle, FiLogIn } from 'react-icons/fi';

const acceptTeamInviteFn = httpsCallable(functions, 'acceptTeamInvite');

function AcceptTeamInvite() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { userId, user, loading: authLoading } = useAuthUser();
  const inviteId = searchParams.get('id');

  const [status, setStatus] = useState('loading'); // loading, accepting, success, error, email_mismatch, no_invite, login_required
  const [errorMessage, setErrorMessage] = useState('');

  const acceptInvite = useCallback(async () => {
    if (!inviteId || !userId) return;
    setStatus('accepting');
    try {
      const result = await acceptTeamInviteFn({ inviteId });
      if (result.data.success) {
        setStatus('success');
        // Clear pending invite from localStorage
        localStorage.removeItem('pendingTeamInvite');
        // Redirect after a moment
        setTimeout(() => navigate('/settings?tab=team'), 2000);
      }
    } catch (err) {
      const code = err?.code || '';
      const msg = err?.message || 'Failed to accept invitation.';
      if (code === 'functions/permission-denied' && msg.includes('different email')) {
        setStatus('email_mismatch');
        setErrorMessage(msg);
      } else if (code === 'functions/not-found') {
        setStatus('error');
        setErrorMessage('This invitation was not found or has been revoked.');
      } else if (code === 'functions/failed-precondition') {
        setStatus('error');
        setErrorMessage(msg);
      } else {
        setStatus('error');
        setErrorMessage(msg);
      }
    }
  }, [inviteId, userId, navigate]);

  useEffect(() => {
    if (authLoading) return;

    if (!inviteId) {
      setStatus('no_invite');
      return;
    }

    if (!userId) {
      // Store invite ID for after login/signup
      localStorage.setItem('pendingTeamInvite', inviteId);
      setStatus('login_required');
      return;
    }

    // Authenticated — try to accept
    acceptInvite();
  }, [authLoading, inviteId, userId, acceptInvite]);

  return (
    <div className="min-h-screen bg-warm-dark flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-warm-gray/10 backdrop-blur-md rounded-[2rem] border border-white/5 shadow-card p-10 text-center">
        {/* Loading */}
        {(status === 'loading' || status === 'accepting') && (
          <>
            <FiLoader className="animate-spin text-4xl text-slate-blue mx-auto mb-4" />
            <h2 className="text-xl font-bold text-cream mb-2">
              {status === 'loading' ? 'Loading...' : 'Accepting Invitation...'}
            </h2>
            <p className="text-sm text-cream/40">Please wait while we process your invitation.</p>
          </>
        )}

        {/* Success */}
        {status === 'success' && (
          <>
            <FiCheckCircle className="text-5xl text-pastel-green mx-auto mb-4" />
            <h2 className="text-xl font-bold text-cream mb-2">Welcome to the Team!</h2>
            <p className="text-sm text-cream/40 mb-4">You've successfully joined the team. Redirecting to settings...</p>
          </>
        )}

        {/* Error */}
        {status === 'error' && (
          <>
            <FiAlertCircle className="text-5xl text-soft-coral mx-auto mb-4" />
            <h2 className="text-xl font-bold text-cream mb-2">Unable to Accept</h2>
            <p className="text-sm text-cream/40 mb-6">{errorMessage}</p>
            <Link to="/settings?tab=team" className="btn btn-primary px-6 py-3 rounded-full text-sm font-bold">
              Go to Settings
            </Link>
          </>
        )}

        {/* Email mismatch */}
        {status === 'email_mismatch' && (
          <>
            <FiAlertCircle className="text-5xl text-amber-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-cream mb-2">Email Mismatch</h2>
            <p className="text-sm text-cream/40 mb-2">
              This invitation was sent to a different email address.
            </p>
            <p className="text-xs text-cream/30 mb-6">
              You're logged in as <strong className="text-cream/50">{user?.email}</strong>.
              Please log in with the email address the invite was sent to.
            </p>
            <Link to="/dashboard" className="btn btn-primary px-6 py-3 rounded-full text-sm font-bold">
              Go to Dashboard
            </Link>
          </>
        )}

        {/* No invite ID */}
        {status === 'no_invite' && (
          <>
            <FiUsers className="text-5xl text-cream/20 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-cream mb-2">No Invitation Found</h2>
            <p className="text-sm text-cream/40 mb-6">This link doesn't contain a valid team invitation.</p>
            <Link to="/dashboard" className="btn btn-primary px-6 py-3 rounded-full text-sm font-bold">
              Go to Dashboard
            </Link>
          </>
        )}

        {/* Not logged in */}
        {status === 'login_required' && (
          <>
            <FiUsers className="text-5xl text-slate-blue mx-auto mb-4" />
            <h2 className="text-xl font-bold text-cream mb-2">Team Invitation</h2>
            <p className="text-sm text-cream/40 mb-6">
              You've been invited to join a team on NextAInsight. Sign in to accept.
            </p>
            <div className="flex flex-col gap-3">
              <Link
                to={`/login?redirect=/team-invite?id=${inviteId}`}
                className="btn btn-primary px-6 py-3 rounded-full text-sm font-bold inline-flex items-center justify-center gap-2"
              >
                <FiLogIn /> Log In
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default AcceptTeamInvite;
